import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import TextInput from "../Common/FormElements/TextInput/TextInput";
import Select from "../Common/FormElements/Select/Select";
import Button from "../Common/FormElements/Button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/fontawesome-free-solid";
import { openRightModal } from "../../REDUX/actions/rightModal";
import NewGuide from "./NewGuide";
import { getGuides } from "../../REDUX/actions/guides";
import { getRanges } from "../../REDUX/actions/range";
import { getCategories } from "../../REDUX/actions/category";
import API from "../../Api/Api";
import { API_END_POINT } from "../../Api/EndPoints";
import { isDefined } from "../../Utils";
import { useTranslation } from "react-i18next";

const FaqViewHeader = ({ setSearchQuery, searchQuery }) => {
  const { guidesLoading } = useSelector((state) => state.guidesReducer);
  const { i18n, t } = useTranslation("common");
  const productTypes = [
    {
      name: t("Products"),
      code: 1,
    },
    {
      name: t("Accessories"),
      code: 2,
    },
    {
      name: t("Communication"),
      code: 3,
    },
    {
      name: t("AS"),
      code: 4,
    },
  ];

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userReducer);
  const { ranges } = useSelector((state) => state.rangeReducer);
  const { categories } = useSelector((state) => state.categoryReducer);

  const [data, setData] = useState({
    types: productTypes,
    categories: [],
    ranges: [],
    models: [],
  });

  const [filters, setFilters] = useState({});

  const modelsAbortRef = useRef(null);
  const rangesCategoriesAbortRef = useRef(null);
  const guidesAbortRef = useRef(null);

  const getModels = async (signal) => {
    try {
      if (isDefined(filters?.type)) {
        const { data } = await API.get(
          `${API_END_POINT}api/products?filters[productType][$eq]=${filters?.type?.code}`,
          { signal },
        );

        setData((currentData) => ({
          ...currentData,
          categories: [],
          ranges: [],
          models: data?.data?.map((item) => ({
            name: item?.attributes?.name,
            code: item?.id,
          })),
        }));
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return;
      console.log(error);
    }
  };

  const handleSearch = (event) => {
    const { value } = event.target;
    setSearchQuery(value);
  };

  const clearSearch = () => {
    setSearchQuery("");
    const localeFilter = i18n.language.includes("en")
      ? { locale: { $eq: "en" } }
      : {
          $and: [
            { $or: [{ locale: { $eq: "fr" } }, { locale: { $null: true } }] },
          ],
        };
    dispatch(
      getGuides({
        ...localeFilter,
        gamme: {
          id: { $eq: filters?.range?.code },
        },
        model: {
          id: { $eq: filters?.model?.code },
          productType: { $eq: filters?.type?.code },
        },
        category: {
          id: { $eq: filters?.category?.code },
        },
      }),
    );
  };
  const handleSearchBtnClick = (e) => {
    if (e) {
      e.preventDefault();
    }
    const localeFilter = i18n.language.includes("en")
      ? { locale: { $eq: "en" } }
      : {
          $and: [
            { $or: [{ locale: { $eq: "fr" } }, { locale: { $null: true } }] },
          ],
        };
    dispatch(
      getGuides({
        ...localeFilter,
        $or: [
          {
            title: {
              $containsi: searchQuery?.trim(),
            },
          },
          {
            description: {
              $containsi: searchQuery?.trim(),
            },
          },
        ],
        gamme: {
          id: { $eq: filters?.range?.code },
        },
        model: {
          id: { $eq: filters?.model?.code },
          productType: { $eq: filters?.type?.code },
        },
        category: {
          id: { $eq: filters?.category?.code },
        },
      }),
    );
  };
  const handleSelectChange = (e) => {
    const { name, value } = e.target;

    setFilters((filters) => {
      const updatedFilters = { ...filters, [name]: value };

      if (name === "type") {
        return { type: value };
      }

      if (name === "category") {
        return { ...updatedFilters, range: undefined, model: undefined };
      }

      if (name === "range") {
        return { ...updatedFilters, model: undefined };
      }

      return updatedFilters;
    });
  };

  useEffect(() => {
    if (guidesLoading) return;
    guidesAbortRef.current?.abort();
    guidesAbortRef.current = new AbortController();
    const signal = guidesAbortRef.current.signal;
    const localeFilter = i18n.language.includes("en")
      ? { locale: { $eq: "en" } }
      : {
          $and: [
            { $or: [{ locale: { $eq: "fr" } }, { locale: { $null: true } }] },
          ],
        };
    dispatch(
      getGuides(
        {
          ...localeFilter,
          $or: [
            {
              title: {
                $containsi: searchQuery?.trim(),
              },
            },
            {
              description: {
                $containsi: searchQuery?.trim(),
              },
            },
            {
              description: {
                $containsi: searchQuery?.trim(),
              },
            },
          ],
          gamme: {
            id: { $eq: filters?.range?.code },
          },
          model: {
            id: { $eq: filters?.model?.code },
            productType: { $eq: filters?.type?.code },
          },
          category: {
            id: { $eq: filters?.category?.code },
          },
        },
        signal,
      ),
    );
  }, [filters, dispatch, i18n.language]);

  useEffect(() => {
    rangesCategoriesAbortRef.current?.abort();
    rangesCategoriesAbortRef.current = new AbortController();
    const signal = rangesCategoriesAbortRef.current.signal;
    dispatch(getRanges(i18n.language, signal));
    dispatch(getCategories(signal));
    return () => {
      rangesCategoriesAbortRef.current?.abort();
      guidesAbortRef.current?.abort();
    };
  }, [dispatch, i18n]);

  useEffect(() => {
    setData((currData) => ({
      ...currData,
      categories,
      ranges: [],
      models: [],
    }));
  }, [filters?.type]);

  useEffect(() => {
    setData((currData) => ({
      ...currData,
      categories,
      ranges: [],
      models: [],
    }));
  }, [categories]);

  useEffect(() => {
    setData((data) => ({
      ...data,
      ranges: filters?.category?.ranges,
      models: [],
    }));
  }, [filters?.category, categories]);

  useEffect(() => {
    setData((data) => ({
      ...data,
      models: ranges?.find((range) => +range?.code === +filters?.range?.code)
        ?.models,
    }));
  }, [filters?.range, ranges, i18n.language]);

  useEffect(() => {
    if ([2, 4, 3]?.includes(filters?.type?.code)) {
      modelsAbortRef.current?.abort();
      modelsAbortRef.current = new AbortController();
      getModels(modelsAbortRef.current.signal);
    }
    return () => {
      modelsAbortRef.current?.abort();
    };
  }, [filters?.type?.code, i18n.language]);

  useLayoutEffect(() => {
    setFilters({});
  }, [i18n.language]);

  return (
    <div className="d-flex justify-content-between align-items-start w-100 ">
      <h2 className="text-blue bold f30 flex-1">{t("Guides")}</h2>
      <div
        className={`row alignH align-items-end justify-content-between ${
          user?.attributes?.userRole === 0 ? "flex-3" : "flex-4"
        }`}
      >
        <div className={`${filters?.type?.code === 1 ? "flex-4" : "flex-3"}`}>
          <form onSubmit={handleSearchBtnClick}>
            <TextInput
              withSearchBtn
              placeholder={t("Search")}
              callback={handleSearch}
              value={searchQuery}
              onSearchBtnClick={handleSearchBtnClick}
              searchButtonType="submit"
              clearSearch={clearSearch}
            />
          </form>
        </div>
        <div className="flex-2 d-flex flex-column align-items-start gap-2">
          <span className="normal f16 text-softBlack">{t("Guides")}</span>
          <Select
            name="type"
            value={filters?.type}
            options={data.types}
            placeholder={t("Select")}
            callback={(e) => handleSelectChange(e)}
            showClear
            resetable
          />
        </div>
        {filters?.type?.code === 1 && (
          <div className="flex-2 d-flex flex-column align-items-start gap-2">
            <span className="normal f16 text-softBlack">{t("Category")}</span>
            <Select
              name="category"
              value={filters?.category}
              options={data.categories.map(({ name, code, ranges }) => ({
                name: t(`${name}`),
                code,
                ranges,
              }))}
              placeholder={t("Select")}
              callback={(e) => handleSelectChange(e)}
              showClear
              resetable
            />
          </div>
        )}
        {filters?.type?.code === 1 && (
          <div className={`flex-2 d-flex flex-column align-items-start gap-2`}>
            <span className="normal f16 text-softBlack">{t("Range")}</span>
            <Select
              name="range"
              value={filters?.range}
              options={data.ranges}
              placeholder={t("Select")}
              callback={(e) => handleSelectChange(e)}
              showClear
              resetable
            />
          </div>
        )}
        <div className="flex-2 d-flex flex-column align-items-start gap-2">
          <span className="normal f16 text-softBlack">{t("Model")}</span>
          <Select
            name="model"
            value={filters?.model}
            options={data.models}
            placeholder={t("Select")}
            callback={(e) => handleSelectChange(e)}
            showClear
            resetable
          />
        </div>
        {[1, 2]?.includes(user?.attributes?.userRole) && (
          <div className="flex-2 d-flex flex-column align-items-start gap-2">
            <span></span>
            <Button
              Icon={() => <FontAwesomeIcon icon={faPlus} size={"xs"} />}
              className="w100"
              onClick={() => dispatch(openRightModal(<NewGuide />))}
            >
              {t("Add new guide")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaqViewHeader;
